/**
 * Capturing where a trainer came from, at first touch (migration 0073).
 *
 * WHAT THIS CAN AND CANNOT SEE — worth being blunt about, because the gap is
 * the whole reason the self-reported column exists next to this one:
 *
 *   web visit to app.nexusdex.ai?utm_source=tiktok    MEASURED
 *   in-app arrival via a universal/deep link           MEASURED
 *   Android install from a Play link with a referrer   MEASURABLE (native work,
 *                                                      not wired yet)
 *   iOS install from the App Store                     NOT MEASURABLE, ever
 *
 * Apple passes no referrer through an App Store install. There is no clever
 * workaround: fingerprint matching is against App Store rules, and the
 * AdServices API only attributes Apple's own paid ads. So for a social short
 * driving iOS installs, asking the trainer is not a lazy fallback — it is the
 * only instrument that exists.
 *
 * FIRST TOUCH WINS
 *   Stored once and never overwritten. Someone who arrives from a short, leaves,
 *   and returns via a Discord link belongs to the short: that is what created
 *   the demand. Last-touch would credit whichever channel sits closest to
 *   signup, which is usually the one that cost nothing.
 */

const STORAGE_KEY = "nx.acq.firstTouch";

export interface FirstTouch {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  referrer: string | null;
  firstSeenAt: number;
}

/** Keep values short and boring — these end up in a GROUP BY. */
function clean(v: string | null | undefined): string | null {
  if (!v) return null;
  const t = v.trim().slice(0, 64);
  return t.length > 0 ? t.toLowerCase() : null;
}

/** Host only. A full referrer URL carries paths and query strings we neither
    need nor want to store. */
export function referrerHost(referrer: string): string | null {
  if (!referrer) return null;
  try {
    const host = new URL(referrer).hostname.replace(/^www\./, "");
    return clean(host);
  } catch {
    return null;
  }
}

export function parseFirstTouch(
  url: string,
  referrer: string,
  now: number,
): FirstTouch | null {
  let params: URLSearchParams;
  try {
    const u = new URL(url);
    // Params may sit in the query OR after the hash, because the app is
    // hash-routed: a shared link is often /#/?utm_source=x and the plain
    // searchParams would miss it entirely.
    params = new URLSearchParams(u.search);
    const hashQuery = u.hash.includes("?") ? u.hash.slice(u.hash.indexOf("?") + 1) : "";
    for (const [k, v] of new URLSearchParams(hashQuery)) {
      if (!params.has(k)) params.set(k, v);
    }
  } catch {
    return null;
  }

  const source = clean(params.get("utm_source") ?? params.get("ref"));
  const medium = clean(params.get("utm_medium"));
  const campaign = clean(params.get("utm_campaign"));
  const host = referrerHost(referrer);
  // An external referrer with no UTM is still a touch worth keeping — it is how
  // an unlabelled share from Discord or Reddit shows up at all.
  const isSelfReferral = host != null && /nexusdex\.ai$/.test(host);
  if (!source && !medium && !campaign && (!host || isSelfReferral)) return null;

  return {
    source,
    medium,
    campaign,
    referrer: isSelfReferral ? null : host,
    firstSeenAt: now,
  };
}

/**
 * Record the first touch if we have not already. Returns whatever is stored.
 *
 * Never overwrites: that is what makes it FIRST touch. Storage failures are
 * swallowed — attribution must not be able to break a page load.
 */
export function captureFirstTouch(
  url: string = typeof window !== "undefined" ? window.location.href : "",
  referrer: string = typeof document !== "undefined" ? document.referrer : "",
  now: number = Date.now(),
): FirstTouch | null {
  const existing = readFirstTouch();
  if (existing) return existing;

  const touch = parseFirstTouch(url, referrer, now);
  if (!touch) return null;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(touch));
  } catch {
    // Not persisted; the in-memory value still reaches this signup attempt.
  }
  return touch;
}

export function readFirstTouch(): FirstTouch | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as FirstTouch;
    return typeof parsed?.firstSeenAt === "number" ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Fold a Google Play install referrer into a first touch (Android only).
 *
 * The API hands back a URL-encoded query string:
 *   "utm_source=tiktok&utm_medium=short&utm_campaign=launch"
 * and for an untagged install:
 *   "utm_source=google-play&utm_medium=organic"
 *
 * That organic string is a REAL ANSWER, not a failure — knowing an install came
 * from browsing the store rather than from a campaign is half the value. It is
 * kept as-is rather than discarded, so organic shows up as its own channel
 * instead of silently swelling the unattributed pile.
 *
 * `clickedAt` is used as the first-touch time when present: the trainer's real
 * first touch was tapping the campaign link, which can be days before the
 * install, and that gap is the drop-off no store console will show you.
 */
export function parsePlayReferrer(
  referrer: string,
  installedAt: number,
  clickedAtSeconds?: number,
): FirstTouch | null {
  if (!referrer || referrer.trim().length === 0) return null;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(referrer);
  } catch {
    return null;
  }

  // Google's ACTUAL organic string is "utm_source=(not set)&utm_medium=(not
  // set)", not the "google-play / organic" the docs suggest: five real
  // accounts landed in the report as source "(not set)" (2026-09-14..18).
  // Translate it here so browsing-the-store shows up as its own channel.
  const source = playOrganic(clean(params.get("utm_source") ?? params.get("ref")), "google-play");
  const medium = playOrganic(clean(params.get("utm_medium")), "organic");
  const campaign = playOrganic(clean(params.get("utm_campaign")), null);
  if (!source && !medium && !campaign) return null;

  return {
    source,
    medium,
    campaign,
    // The Play Store is the carrier here, not the origin; recording it as a
    // referrer host would compete with the real utm_source in the report.
    referrer: null,
    firstSeenAt:
      clickedAtSeconds && clickedAtSeconds > 0 ? clickedAtSeconds * 1000 : installedAt,
  };
}

/**
 * Ask the native layer for the Play install referrer and store it as first
 * touch, if nothing has been stored yet.
 *
 * Runs at most once per device: the answer never changes for an install, and
 * the Play service is a bound connection worth opening only once. Silent on
 * every failure path — no Play Services, no plugin (iOS/web), service
 * unavailable — because attribution must not affect a first launch.
 */
/** Play's "(not set)" placeholder means organic; anything else passes through. */
function playOrganic(value: string | null, organic: string | null): string | null {
  if (value == null) return null;
  return value === "(not set)" ? organic : value;
}

export async function captureAndroidInstallReferrer(now: number = Date.now()): Promise<FirstTouch | null> {
  if (readFirstTouch()) return readFirstTouch();
  try {
    const { Capacitor, registerPlugin } = await import("@capacitor/core");
    if (Capacitor.getPlatform() !== "android") return null;

    const plugin = registerPlugin<{
      getReferrer(): Promise<{
        available: boolean;
        referrer?: string;
        referrerClickTime?: number;
        installBeginTime?: number;
      }>;
    }>("InstallReferrer");

    const res = await plugin.getReferrer();
    if (!res?.available || !res.referrer) return null;

    const touch = parsePlayReferrer(res.referrer, now, res.referrerClickTime);
    if (!touch) return null;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(touch));
    } catch {
      /* not persisted; still returned for this session */
    }
    return touch;
  } catch {
    // Plugin absent (web/iOS) or the call threw. Nothing to attribute.
    return null;
  }
}

/** The fixed answer list for "How did you hear about us?".
 *
 *  Fixed, not free text: this is the only attribution iOS store installs will
 *  ever have, and a thousand spellings of "tiktok" would waste it.
 */
export const HEARD_ABOUT_OPTIONS = [
  { key: "tiktok", label: "TikTok" },
  { key: "youtube", label: "YouTube" },
  { key: "reddit", label: "Reddit" },
  { key: "discord", label: "Discord" },
  { key: "friend", label: "A friend told me" },
  { key: "app-store-search", label: "Searching the App Store" },
  { key: "other", label: "Somewhere else" },
] as const;

export type HeardAbout = (typeof HEARD_ABOUT_OPTIONS)[number]["key"];
